import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../../context/AuthContext';
import { useBilling } from '../../../context/BillingContext';
import { getDashboardPathForUser } from '../../../utils/dashboardRouting';
import { isAdminSession, isUserPreviewActive } from '../../admin/utils/adminPreview';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { subscription, loading: billingLoading, trialDaysRemaining, isExpired } = useBilling();
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  // Wait for both auth and billing to finish loading, and for subscription to be loaded (not undefined)
  if (authLoading || billingLoading || typeof subscription === 'undefined') {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Block access when trial has ended (time expired but job hasn't flipped status yet, or already flipped to expired)
  const trialEnded = subscription?.status === 'trialing' && trialDaysRemaining === null;
  if ((trialEnded || isExpired) && location.pathname !== '/billing') {
    return <Navigate to="/billing" replace />;
  }

  const dashboardPath = getDashboardPathForUser(
    subscription?.plan_tier,
    user?.organization_type,
    user?.is_enterprise_member,
  );

  const adminPreviewEnabled = location.search.includes('adminPreview=1') || isUserPreviewActive();

  if (isAdminSession() && !adminPreviewEnabled && !location.pathname.startsWith('/admin')) {
    const isUserDashboardRoute =
      location.pathname === '/app' ||
      location.pathname === '/search' ||
      location.pathname === '/history' ||
      location.pathname === '/search-history' ||
      location.pathname.startsWith('/dashboard') ||
      location.pathname.startsWith('/enterprise/dashboard') ||
      location.pathname.startsWith('/startup/dashboard') ||
      location.pathname.startsWith('/government/dashboard');

    if (isUserDashboardRoute) {
      const adminFallback = location.pathname.includes('/regulations-breakdown')
        ? '/admin/dashboard/charts'
        : '/admin/dashboard';
      return <Navigate to={adminFallback} replace />;
    }
  }

  // Only the generic dashboard is forwarded to the account's own variant.
  //
  // This used to forward /app, /search, /history and /search-history as well,
  // so on any plan with a segment dashboard — startup, government, enterprise —
  // clicking Search in the menu opened the dashboard instead of search, and
  // Chat and History did the same. A customer on Free, which has no variant,
  // got the page they asked for; the same click on Business did not. Those are
  // real destinations and the menu points at them.
  if (user && dashboardPath && location.pathname === '/dashboard') {
    return <Navigate to={dashboardPath} replace />;
  }

  return <>{children}</>;
};
